import useDebouncedValue from "@pathwright/ui/src/components/hooks/useDebouncedValue"
import uniq from "lodash/uniq"
import { useEffect, useMemo, useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import useControlledQueryParams, {
  decodeQueryParams
} from "./useControlledQueryParams"

export type ControlledBrowseQueryParamsOptions = {
  /**
   * True when `pathname` is the browse root for this view (filters apply).
   * Used to re-sync from the URL when navigating back into browse under a
   * cover/modal that stays mounted.
   */
  isRootPath?: (pathname: string) => boolean
}

const defaultIsLibraryRootPath = (pathname: string) =>
  pathname === "/library/" || pathname === "/library"

/**
 * Bidirectional sync of browse facet names with `?category=` / `?author=` /
 * `?search=`. Shared by Library and Store — filters use names (not ids)
 * because Algolia facets are name-based and cover pills link by name.
 */
const useControlledBrowseQueryParams = (
  options: ControlledBrowseQueryParamsOptions = {}
) => {
  const { isRootPath = defaultIsLibraryRootPath } = options

  // debounced search
  const [search, debouncedSearch, debouncingSearch, handleDebounceSearch] =
    useDebouncedValue(200, null)

  // author names for browse filters
  const [selectedAuthorNames, setSelectedAuthorNames] = useState<string[]>([])
  // category names for browse filters
  const [selectedCategoryNames, setSelectedCategoryNames] = useState<string[]>(
    []
  )

  // useMemo necessary for avoiding infinite loop of setState, probably
  // avoidable by optimizing useControlledQueryParams, but either way
  // useMemo here is probably more efficient.
  const paramsObj = useMemo(
    () => ({
      author: selectedAuthorNames,
      category: selectedCategoryNames,
      search: debouncedSearch
    }),
    [selectedAuthorNames, selectedCategoryNames, debouncedSearch]
  )

  const controlledQueryParams = useControlledQueryParams(paramsObj)

  // Targeted refresh: when navigating INTO the browse root, re-sync state from URL
  // This is to ensure we apply the query params from the URL to the state.
  const location = useLocation()
  const prevPathnameRef = useRef(location.pathname)
  const isRootPathRef = useRef(isRootPath)
  isRootPathRef.current = isRootPath

  useEffect(() => {
    const pathname = location.pathname
    const isRoot = isRootPathRef.current(pathname)
    const wasRoot = isRootPathRef.current(prevPathnameRef.current)

    if (isRoot && !wasRoot) {
      const decoded = decodeQueryParams({
        search: debouncedSearch,
        author: selectedAuthorNames,
        category: selectedCategoryNames
      }) as unknown as {
        search: string | null
        author: string[] | null
        category: string[] | null
      }

      const searchQueryParam = decoded.search || null
      const authorFromUrl = decoded.author || []
      const categoryFromUrl = decoded.category || []

      if (search !== searchQueryParam) {
        // Allow URL to override when different
        handleDebounceSearch(search || searchQueryParam)
      }
      if (uniq([...authorFromUrl, ...selectedAuthorNames]).length) {
        setSelectedAuthorNames(authorFromUrl)
      }
      if (uniq([...categoryFromUrl, ...selectedCategoryNames]).length) {
        setSelectedCategoryNames(categoryFromUrl)
      }
    }

    prevPathnameRef.current = pathname
  }, [location.pathname])

  useEffect(() => {
    // Prevents glitch when clearing search via "View full Library"
    if (debouncingSearch) return

    const {
      search: searchQueryParam,
      author,
      category
    } = controlledQueryParams as {
      search: string
      author: string[]
      category: string[]
    }

    if (search !== searchQueryParam) {
      // Allow searchQueryParam to override search if search is falsey, for instance,
      // if on first mount.
      handleDebounceSearch(search || searchQueryParam)
    }
    // only update selectedAuthorNames if authors in query params differs from local state
    if (uniq([...author, ...selectedAuthorNames]).length) {
      setSelectedAuthorNames(author || [])
    }
    // only update selectedCategoryNames if categories in query params differs from local state
    if (uniq([...category, ...selectedCategoryNames]).length) {
      setSelectedCategoryNames(category || [])
    }
  }, [controlledQueryParams])

  return {
    search,
    debouncedSearch,
    debouncingSearch,
    handleDebounceSearch,
    selectedAuthorNames,
    setSelectedAuthorNames,
    selectedCategoryNames,
    setSelectedCategoryNames,
    controlledQueryParams
  }
}

export default useControlledBrowseQueryParams
